<template>
  <q-page padding>
    <q-toolbar class="q-px-md">
      <q-toolbar-title class="text-weight-medium"> Dashboard </q-toolbar-title>

      <div>
        <div class="row">
          <date-input
            buttons
            class="q-mr-sm"
            dense
            outlined
            :label="$t('period')"
            :v-model="period"
            @update="
              (val) => {
                period = val;
              }
            "
            today-btn
            range
            label-color="primary"
          />
          <q-btn-dropdown
            no-caps
            outline
            class="q-mr-sm"
            content-style="width: 380px"
            :menu-offset="[0, 10]"
            :label="$t('filters')"
            @before-show="retrieveSelectOptions"
          >
            <q-list separator>
              <div v-for="(filter, index) in Object.values(filters)" :key="index">
                <q-item clickable @click="filter.editing = !filter.editing" class="q-pa-md">
                  <q-item-section>
                    <div :class="isFilterActive(filter) ? 'text-bold' : ''">
                      {{ isFilterActive(filter) ? '->' : '' }}
                      {{ getFilterLabel(filter.name) }}
                    </div>
                  </q-item-section>
                  <q-item-section side>
                    <q-icon :name="filter.editing ? 'keyboard_arrow_up' : 'keyboard_arrow_down'" />
                  </q-item-section>
                </q-item>
                <div class="col-12 q-px-md q-py-sm" v-if="filter.editing">
                  <q-select
                    v-model="filter.value"
                    :options="filter.name == 'statuses' ? statusOptions : filter.optionsFiltered"
                    map-options
                    emit-value
                    :label="getFilterLabel(filter.name)"
                    :option-label="filter.option_label"
                    :option-value="filter.option_value"
                    outlined
                    clearable
                    use-input
                    :loading="filterOptionsLoading && filter.name !== 'statuses'"
                    :multiple="filter.name !== 'channels'"
                    :use-chips="filter.name !== 'channels'"
                    @filter="(val, update) => update(selectFilter(val, index, filter.option_label))"
                    @clear="filter.value = filter.name !== 'channels' ? [] : ''"
                  />
                </div>
              </div>
            </q-list>

            <div class="flex justify-center q-pa-md q-mt-sm">
              <q-btn
                :label="$t('reset_filters')"
                unelevated
                outline
                color="primary"
                style="width: 160px"
                @click="resetFilters"
                no-caps
              />
            </div>
          </q-btn-dropdown>

          <q-btn
            color="primary"
            padding="7px 7px"
            unelevated
            icon="search"
            dense
            :loading="dashboardLoading"
            @click="getDashboard"
          />
        </div>
      </div>
    </q-toolbar>
    <!-- <dashboard-main :editGrid="edit" /> -->
    <dashboard-affiliate
      :summary="summary"
      :campaign-metrics="campaignMetrics"
      :exclusive-campaigns="exclusiveCampaigns"
      :main-campaigns="mainCampaigns"
      :transactions-by-day="transactionsByDay"
      :conversion-status="conversionStatus"
      :commission-by-month="commissionByMonth"
    />
  </q-page>
</template>

<style></style>

<script>
import DashboardAffiliate from '@/components/dashboard/DashboardAffiliate.vue';
import DateInput from '@/widgets/inputsGlobal/DateInput.vue';
import AdvertiserService from '@/services/AdvertiserService.js';
import AffiliateService from '@/services/AffiliateService.js';
import DashboardResume from '@/services/DashboardResume.js';

export default {
  name: 'PageDashboard',
  components: { DashboardAffiliate, DateInput },
  mixins: [AdvertiserService, AffiliateService, DashboardResume],
  mounted() {
    this.getStaticDashboardData();
    this.getDashboard();
  },
  created() {
    this.period = `${this.$moment().subtract(7, 'days').format('L')} - ${this.$moment().format('L')}`;
  },

  computed: {
    statusOptions() {
      return [
        { label: this.$t('refused'), value: 0 },
        { label: this.$t('pending'), value: 1 },
        { label: this.$t('approved'), value: 2 }
      ];
    },
    campaignLabel() {
      return this.$t('campaign');
    },

    channelLabel() {
      return this.$t('channel');
    }
  },

  data() {
    return {
      //   edit: false
      period: '',
      //   ignoreTestCampaigns: '1',
      summary: {
        conversionValue: 0,
        commissionValue: 0,
        conversionAmount: 0
      },
      campaignMetrics: [],
      exclusiveCampaigns: [],
      mainCampaigns: {
        higherCommission: [],
        convertMore: []
      },
      transactionsByDay: [],
      conversionStatus: [],
      commissionByMonth: [],
      dashboardLoading: false,
      dashboardRequestId: 0,
      filterOptionsLoaded: false,
      filterOptionsLoading: false,

      filters: {
        status: {
          label: 'Status',
          name: 'statuses',
          value: [],
          editing: false
        },
        campaign: {
          name: 'campaigns',
          value: [],
          editing: false,
          options: [],
          optionsFiltered: [],
          option_label: 'campaign_name',
          option_value: 'campaign_id'
        },
        channel: {
          name: 'channels',
          value: '',
          editing: false,
          options: [],
          optionsFiltered: [],
          option_label: 'name',
          option_value: 'id'
        }
      }
    };
  },

  methods: {
    getFilterLabel(filterName) {
      switch (filterName) {
        case 'statuses':
          return 'Status';
        case 'campaigns':
          return this.campaignLabel;
        case 'channels':
          return this.channelLabel;
        default:
          return '';
      }
    },

    isFilterActive(filter) {
      if (Array.isArray(filter.value)) {
        return filter.value.length > 0;
      }
      return !!filter.value;
    },

    periodString() {
      var [startDate, endDate] = this.period.split(' - ');
      return `start_date%3D${this.formatToEn(startDate)}%26end_date%3D${this.formatToEn(endDate)}`;
    },

    staticCampaignPeriod() {
      return `start_date=${this.$moment()
        .subtract(3, 'months')
        .startOf('month')
        .format('YYYY-MM-DD')}%26end_date=${this.$moment()
        .subtract(1, 'months')
        .endOf('month')
        .format('YYYY-MM-DD')}`;
    },

    dashboardParams() {
      let params = this.periodString();
      let statusParams = params;

      if (this.filters.status.value.length) {
        params += `%26validation_status%3D${this.filters.status.value.join('%2C')}`;
        statusParams += `%26statuses%3D${this.filters.status.value.join('%2C')}`;
      }
      if (this.filters.campaign.value.length) {
        params += `%26campaign_id%3D${this.filters.campaign.value.join('%2C')}`;
        statusParams += `%26campaigns%3D${this.filters.campaign.value.join('%2C')}`;
      }
      if (this.filters.channel.value) {
        params += `%26site_id%3D${this.filters.channel.value}`;
        statusParams += `%26channels%3D${this.filters.channel.value}`;
      }

      const affiliateId = this.user.affiliate?.id || this.user.affiliate_id;
      statusParams += `%26affiliate_id%3D${affiliateId}%26ignore_test%3D0`;

      return { params, statusParams };
    },

    async runDashboardRequest(requestId, request, onSuccess) {
      try {
        const response = await request;
        if (requestId === this.dashboardRequestId) {
          onSuccess(response.data);
        }
      } catch (error) {
        if (requestId === this.dashboardRequestId) {
          this.errorNotify(error);
        }
      }
    },

    async getDashboard() {
      const requestId = ++this.dashboardRequestId;
      const { params, statusParams } = this.dashboardParams();
      this.dashboardLoading = true;

      await Promise.all([
        this.runDashboardRequest(requestId, this.getDashboardSummaryAffiliate(params), (summary) => {
          this.summary = {
            conversionValue: summary.transaction_amount,
            commissionValue: summary.affiliate_commission_cost,
            conversionAmount: summary.quantity_transactions
          };
        }),
        this.runDashboardRequest(requestId, this.getDashboardCampaignsAffiliate(params), (data) => {
          this.campaignMetrics = data;
        }),
        this.runDashboardRequest(requestId, this.getTransactionsByDayAffiliate(params), (data) => {
          this.transactionsByDay = data;
        }),
        this.runDashboardRequest(requestId, this.getCommissionByStatus(`?${statusParams}`), (data) => {
          this.conversionStatus = data;
        }),
        this.runDashboardRequest(requestId, this.getAffiliateMonthlyCommissionResume(`?${params}`), (data) => {
          this.commissionByMonth = data;
        })
      ]);

      if (requestId === this.dashboardRequestId) {
        this.dashboardLoading = false;
      }
    },

    async getStaticDashboardData() {
      const results = await Promise.allSettled([
        this.getDashboardExclusiveCampaigns(),
        this.getCampaignsHigherCommission(),
        this.getCampaignsConvertMore(this.staticCampaignPeriod())
      ]);

      if (results[0].status === 'fulfilled') this.exclusiveCampaigns = results[0].value.data;
      if (results[1].status === 'fulfilled') this.mainCampaigns.higherCommission = results[1].value.data;
      if (results[2].status === 'fulfilled') this.mainCampaigns.convertMore = results[2].value.data;

      const rejected = results.find((result) => result.status === 'rejected');
      if (rejected) this.errorNotify(rejected.reason);
    },

    async retrieveSelectOptions() {
      if (this.filterOptionsLoaded || this.filterOptionsLoading) return;

      try {
        this.filterOptionsLoading = true;
        const options = await Promise.all([
          this.getCampaignListAffiliate(this.user.affiliate_id),
          this.getAffiliateSiteActiveListByID(this.user.affiliate_id)
        ]);
        this.filters['campaign'].options = this.filters['campaign'].optionsFiltered = options[0].data;
        this.filters['channel'].options = this.filters['channel'].optionsFiltered = options[1].data;
        this.filterOptionsLoaded = true;
      } catch (error) {
        this.errorNotify(error);
      } finally {
        this.filterOptionsLoading = false;
      }
    },

    selectFilter(val, index, prop = null) {
      const needle = val.toLocaleLowerCase();
      const filters = Object.values(this.filters);
      filters[index].optionsFiltered = needle
        ? filters[index].options.filter((v) =>
            prop ? v[prop].toLocaleLowerCase().includes(needle) : v.toLocaleLowerCase().includes(needle)
          )
        : filters[index].options;
    },

    resetFilters() {
      let reload = false;
      Object.keys(this.filters).forEach((key) => {
        if (this.isFilterActive(this.filters[key])) {
          reload = true;
        }
        if (Array.isArray(this.filters[key].value)) {
          this.filters[key].value = [];
        } else {
          this.filters[key].value = '';
        }
      });
      if (reload) this.getDashboard();
      this.filters.status.editing = false;
      this.filters.campaign.editing = false;
      this.filters.channel.editing = false;
    }
  }
};
</script>
